import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { CollapsibleSectionComponent } from '@brightnav/ui';
import { ScrollRegionComponent } from '@brightside/angular-bds';
import type { AccountDetailDialogData } from '../account-detail.model';
import { canOpenAccountDetail } from '../account-path-type';
import { AccountsStore } from '../accounts.store';
import { AccountsAnalytics } from '../analytics/accounts-analytics';
import { AccountDetailDialogComponent } from '../detail/account-detail-dialog.component';
import {
  formatAccountBalance,
  formatAccountField,
  formatAccountLast4,
  isErrorStatus,
  statusTagSeverity,
} from '../accounts-display';
import {
  filterInventoryAccounts,
  filtersAreDefault,
  GROUP_BY_OPTIONS,
  groupInventoryAccounts,
  statusFilterOptions,
  typeFilterOptions,
} from '../accounts-inventory-filter';
import {
  ACCOUNTS_STATUS_OPEN,
  ACCOUNTS_TYPE_ALL,
  type AccountInventoryRow,
  type AccountsGroupBy,
  type AccountsSortColumn,
  type AccountsStatusFilter,
  type AccountsTypeFilter,
} from '../accounts.model';
import { AccountsSkeletonComponent } from './accounts-skeleton.component';

const SORT_COLUMNS: readonly AccountsSortColumn[] = [
  'name',
  'institution',
  'type',
  'status',
  'balance',
];

type AccountInventoryTableRow = AccountInventoryRow & {
  /** Display-only key used by PrimeNG grouping; never replaces an AIM field. */
  __groupKey?: string;
};

function isSortColumn(field: string | undefined): field is AccountsSortColumn {
  return !!field && (SORT_COLUMNS as readonly string[]).includes(field);
}

/**
 * Read-only Accounts inventory: search, Status/Type/Group by, sort, banner.
 * Provided store loads on mount (tab enter) from the selected client guid.
 */
@Component({
  selector: 'bn-accounts-inventory',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    Button,
    InputText,
    Select,
    TableModule,
    Tag,
    AccountsSkeletonComponent,
    CollapsibleSectionComponent,
    ScrollRegionComponent,
    AccountDetailDialogComponent,
  ],
  providers: [AccountsStore],
  templateUrl: './accounts-inventory.component.html',
  styleUrl: './accounts-inventory.component.scss',
})
export class AccountsInventoryComponent {
  readonly store = inject(AccountsStore);
  private readonly _analytics = inject(AccountsAnalytics);

  readonly detail = signal<AccountDetailDialogData | null>(null);
  readonly detailOpen = signal(false);

  readonly search = signal('');
  readonly statusFilter = signal<AccountsStatusFilter>(ACCOUNTS_STATUS_OPEN);
  readonly typeFilter = signal<AccountsTypeFilter>(ACCOUNTS_TYPE_ALL);
  readonly groupBy = signal<AccountsGroupBy>('none');
  readonly sortField = signal<AccountsSortColumn | null>(null);
  readonly sortOrder = signal<1 | -1>(1);

  readonly formatLast4 = formatAccountLast4;
  readonly formatBalance = formatAccountBalance;
  readonly formatField = formatAccountField;
  readonly statusSeverity = statusTagSeverity;
  readonly isErrorStatus = isErrorStatus;
  readonly groupByOptions = GROUP_BY_OPTIONS;

  readonly showTotal = computed(
    () =>
      !this.store.loading() &&
      !this.store.error() &&
      !!this.store.sessionGuid()
  );

  readonly showingInventory = computed(() => !!this.store.sessionGuid());

  readonly statusOptions = computed(() => statusFilterOptions(this.store.accounts()));
  readonly typeOptions = computed(() => typeFilterOptions(this.store.accounts()));

  readonly filtersDefault = computed(() =>
    filtersAreDefault(this.search(), this.statusFilter(), this.typeFilter(), this.groupBy())
  );

  readonly visibleRows = computed(() => {
    const rows = filterInventoryAccounts(this.store.accounts(), {
      search: this.search(),
      status: this.statusFilter(),
      type: this.typeFilter(),
    });
    const field = this.sortField();
    if (!field) {
      return rows;
    }
    const dir = this.sortOrder();
    return [...rows].sort((a, b) => this._compare(a, b, field) * dir);
  });

  readonly displayedGroups = computed(() =>
    groupInventoryAccounts(this.visibleRows(), this.groupBy())
  );

  readonly showGroups = computed(() => this.groupBy() !== 'none');

  readonly tableRows = computed<AccountInventoryTableRow[]>(() => {
    const field = this.groupBy();
    if (field === 'none') {
      return this.visibleRows();
    }
    return this.displayedGroups().flatMap((group) =>
      group.rows.map((row) => ({ ...row, __groupKey: group.key }))
    );
  });

  readonly noSearchMatch = computed(
    () =>
      !this.store.loading() &&
      !this.store.error() &&
      this.showingInventory() &&
      this.store.accounts().length > 0 &&
      this.visibleRows().length === 0
  );

  private _lastViewedKey: string | null = null;
  private _filtersGuid: string | null | undefined;

  constructor() {
    effect(() => {
      const guid = this.store.sessionGuid();
      untracked(() => {
        if (this._filtersGuid === guid) {
          return;
        }
        this._filtersGuid = guid;
        this.resetFilters();
        this.closeDetails();
      });
    });

    effect(() => {
      const guid = this.store.sessionGuid();
      const loading = this.store.loading();
      const error = this.store.error();
      const total = this.store.accounts().length;
      if (!guid || loading || error) {
        return;
      }
      const key = `${guid}:${total}`;
      untracked(() => {
        if (this._lastViewedKey !== key) {
          this._lastViewedKey = key;
          this._analytics.viewed(total);
        }
      });
    });
  }

  onSearchChange(value: string): void {
    this.search.set(value);
  }

  onStatusChange(value: AccountsStatusFilter): void {
    this.statusFilter.set(value);
  }

  onTypeChange(value: AccountsTypeFilter): void {
    this.typeFilter.set(value);
  }

  onGroupByChange(value: AccountsGroupBy): void {
    this.groupBy.set(value);
  }

  resetFilters(): void {
    this.search.set('');
    this.statusFilter.set(ACCOUNTS_STATUS_OPEN);
    this.typeFilter.set(ACCOUNTS_TYPE_ALL);
    this.groupBy.set('none');
  }

  onSort(event: { field?: string; order?: number }): void {
    if (!isSortColumn(event.field)) {
      return;
    }
    this.sortField.set(event.field);
    this.sortOrder.set(event.order === -1 ? -1 : 1);
  }

  openDetails(row: AccountInventoryRow): void {
    if (!this.canOpenDetails(row)) {
      return;
    }
    const guid = this.store.sessionGuid();
    if (!guid) {
      return;
    }
    this.detail.set({
      guid,
      id: row.id as string,
      inventoryType: row.type as string,
      name: formatAccountField(row.name),
    });
    this.detailOpen.set(true);
  }

  canOpenDetails(row: AccountInventoryRow): boolean {
    return (
      typeof row.id === 'string' &&
      row.id.trim() !== '' &&
      typeof row.type === 'string' &&
      canOpenAccountDetail(row.type)
    );
  }

  closeDetails(): void {
    this.detailOpen.set(false);
    this.detail.set(null);
  }

  onDetailVisibleChange(open: boolean): void {
    this.detailOpen.set(open);
    if (!open) {
      this.detail.set(null);
    }
  }

  groupCount(row: AccountInventoryTableRow): number {
    const field = this.groupBy();
    if (field === 'none') {
      return this.visibleRows().length;
    }
    const key = row.__groupKey ?? String(row[field] ?? '—');
    return this.displayedGroups().find((group) => group.key === key)?.count ?? 0;
  }

  groupLabel(row: AccountInventoryTableRow): string {
    const field = this.groupBy();
    if (field === 'none') {
      return '';
    }
    return (row.__groupKey ?? String(row[field] ?? '—')).toUpperCase();
  }

  private _compare(
    a: AccountInventoryRow,
    b: AccountInventoryRow,
    field: AccountsSortColumn
  ): number {
    if (field === 'balance') {
      const aBalance = typeof a.balance === 'number' ? a.balance : Number.NEGATIVE_INFINITY;
      const bBalance = typeof b.balance === 'number' ? b.balance : Number.NEGATIVE_INFINITY;
      return aBalance - bBalance;
    }
    return formatAccountField(a[field]).localeCompare(formatAccountField(b[field]), 'en-US', {
      sensitivity: 'base',
    });
  }
}
